import {GridColDef} from '@mui/x-data-grid';
import {t} from 'i18next';
import {convertToUTCDate} from '../../utils/formatters';
import {CustomMenuItem} from '../common/arr-menu/ArrMenu';
import {decodeHtmlEntities} from '../../utils/utils';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import {ExtendedTreeItemProps} from '../../shared/interfaces';
import CellWrapper from '../common/arr-data-grid-pro/filter-components/cell-wrapper/CellWrapper';
import {useEffect, useRef, useState} from 'react';
import ArrTooltip from '../common/arr-tooltip/ArrTooltip';

export const savedSearchColumns = (): GridColDef[] => [
  {
    field: 'searchID',
    headerName: t('savedSearch.savedSearchFilterGrid.searchID'),
    headerClassName: 'typo-text label2_size',
    width: 154,
    flex: 1,
  },
  {
    field: 'searchName',
    headerName: t('savedSearch.savedSearchFilterGrid.searchName'),
    headerClassName: 'typo-text label2_size',
    width: 320,
    flex: 1,
    renderCell: (params) => {
      const textRef = useRef<HTMLSpanElement>(null);
      const [isTruncated, setIsTruncated] = useState(false);

      const decodedText = decodeHtmlEntities(params.row.searchName);

      const checkOverflow = () => {
        const el = textRef.current;
        if (!el) return;
        setIsTruncated(el.scrollHeight > el.clientHeight);
      };

      useEffect(() => {
        checkOverflow();

        const resizeObserver = new ResizeObserver(checkOverflow);
        if (textRef.current) {
          resizeObserver.observe(textRef.current);
        }

        return () => resizeObserver.disconnect();
      }, [decodedText]);

      return (
        <div
          className="saved-search-name-cell"
          data-testid={`saved-search-utils-${params.id}`}
        >
          <span
            ref={textRef}
            className={`saved-search-name-text ${isTruncated ? 'saved-search-name-truncated' : ''}`}
            data-testid={`saved-search-utils-searhName-${params.id}`}
          >
            {decodedText}
          </span>

          {isTruncated && (
            <ArrTooltip
              content={decodedText}
              hideInfoIconOnEmptyChildren
              labelId={`tooltiip-saved-search-utils-searhName-${params.id}`}
              className="saved-search-name-more-wrapper"
            >
              <span
                className="saved-search-name-more"
                data-testid={`saved-search-utils-searchName-more-${params.id}`}
              >
                {t('common.more')}
              </span>
            </ArrTooltip>
          )}
        </div>
      );
    },
  },

  {
    field: 'result',
    headerName: t('savedSearch.savedSearchFilterGrid.grid.countResults'),
    headerClassName: 'typo-text label2_size',
    width: 154,
    flex: 1,
  },
  {
    field: 'families',
    headerName: t('savedSearch.savedSearchFilterGrid.grid.familiedIncluded'),
    headerClassName: 'typo-text label2_size',
    width: 154,
    flex: 1,
  },
  {
    field: 'status',
    headerName: t('savedSearch.savedSearchFilterGrid.grid.status'),
    headerClassName: 'typo-text label2_size',
    width: 154,
    flex: 1,
  },
  {
    field: 'submitTime',
    headerName: t('savedSearch.savedSearchFilterGrid.grid.submitTime'),
    width: 154,
    flex: 1,
    renderCell: (params) => {
      const dateValue = convertToUTCDate(params.row.submitTime);
      return (
        <CellWrapper
          toolTipContent={dateValue}
          cellParams={params}
          dataTestId={`tooltip-saved-search-utils-submit-time-${params.id}`}
        >
          <span
            // title={dateValue}
            data-testid={`saved-search-utils-submit-time-${params.id}`}
          >
            {dateValue}
          </span>
        </CellWrapper>
      );
    },
  },
  {
    field: 'lastSubmittedBy',
    headerName: t('savedSearch.savedSearchFilterGrid.grid.submittedBy'),
    headerClassName: 'typo-text label2_size',
    width: 154,
    flex: 1,
  },

  {
    field: 'lastCompleteTime',
    headerName: t('savedSearch.savedSearchFilterGrid.grid.completeTime'),
    width: 154,
    flex: 1,
    renderCell: (params) => {
      const dateValue = convertToUTCDate(params.row.lastCompleteTime);
      return (
        <CellWrapper
          toolTipContent={dateValue}
          cellParams={params}
          dataTestId={`tooltip-saved-search-utils-last-completed-${params.id}`}
        >
          <span
            // title={dateValue}
            data-testid={`saved-search-utils-last-completed-${params.id}`}
          >
            {dateValue}
          </span>
        </CellWrapper>
      );
    },
  },
  // {/* This code needs to uncommit for future release 1.1 */}
  // {
  //   field: 'index',
  //   headerName: t('savedSearch.savedSearchFilterGrid.grid.index'),
  //   headerClassName: 'typo-text label2_size',
  //   width: 154,
  //   flex: 1,
  // },
  {
    field: 'emailThreadedIncluded',
    headerName: t('savedSearch.savedSearchFilterGrid.grid.emailThreaty/n'),
    headerClassName: 'typo-text label2_size',
    width: 154,
    flex: 1,
  },
  {
    field: 'nearDuplicatesIncluded',
    headerName: t(
      'savedSearch.savedSearchFilterGrid.grid.nearDuplicatesIncluded'
    ),
    headerClassName: 'typo-text label2_size',
    width: 154,
    flex: 1,
  },
  {
    field: 'threadDocCount',
    headerName: t('savedSearch.savedSearchFilterGrid.grid.emailThreatCount'),
    headerClassName: 'typo-text label2_size',
    width: 154,
    flex: 1,
  },
  {
    field: 'familyDocCount',
    headerName: t('savedSearch.savedSearchFilterGrid.grid.familyCount'),
    headerClassName: 'typo-text label2_size',
    width: 154,
    flex: 1,
  },
];

export const statusDropdown = [
  {
    id: 'COMPLETED',
    name: 'Completed',
  },
  {
    id: 'ERROR',
    name: 'Error',
  },
  {
    id: 'FAILED',
    name: 'Failed',
  },
  {
    id: 'INPROGRESS',
    name: 'In Progress',
  },
  {
    id: 'PENDING',
    name: 'Pending',
  },
];

export const savedSearchMoreMenu = (
  roleId: number,
  groupId: string,
  isTree: boolean = false,
  isGroup: boolean = false
): CustomMenuItem[] => {
  let menuItems: CustomMenuItem[] = [
    {label: 'Edit Search', id: 'edit'},
    {label: 'Rename', id: 'rename'},
    {label: 'Move', id: 'move'},
    {label: 'Share', id: 'share'},
    {label: 'Delete', id: 'delete'},
  ];

  // Filter based on role and groupId
  if (roleId === 3 && groupId !== '-1') {
    menuItems = menuItems.filter((item) =>
      ['edit', 'rename', 'move'].includes(item.id)
    );
  }

  if (roleId === 2 && groupId !== '-1') {
    menuItems = menuItems.filter((item) =>
      ['edit', 'delete', 'rename', 'move'].includes(item.id)
    );
  }

  // Prepend tree-specific options
  if (isTree) {
    const treeOptions: CustomMenuItem[] = [
      {label: 'Add New Group', id: 'add_group'},
      {label: 'Batch Upload', id: 'batch_upload'},
    ];

    menuItems = menuItems.filter((item) => item.id !== 'edit');
    if (roleId === 2 && groupId !== '-1') {
      menuItems = menuItems.filter((item) =>
        ['delete', 'rename', 'move'].includes(item.id)
      );
    }

    // Add tree options in front
    menuItems = [...treeOptions, ...menuItems];

    if (groupId === '#') {
      return treeOptions;
    }

    if (isGroup) {
      menuItems = menuItems.filter((item) => item.id !== 'edit');
    } else if (!isGroup) {
      return menuItems;
    }
  }

  return menuItems;
};

export interface IUploadFileData {
  'Search Name': string;
  'Document Content': string;
  'Concept Search Text': string;
  'Minimum Confidence Score for Concept Searches (%) (If Blank, System Default is 70%)': string;
  WorkProduct: string;
  Analytics: string;
  'Include Family Members (If Blank, Default is N)': string;
}

//function to validation bulk upload file headers.
export const validateHeaders = (
  headersRow: string[],
  expectedHeaders: string[]
): boolean => {
  if (headersRow.length !== expectedHeaders.length) {
    return false;
  }

  return expectedHeaders.every((header, index) => headersRow[index] === header);
};

// function to validate the row is empty or not.
export const isRowEmpty = (
  row: IUploadFileData,
  headers: string[]
): boolean => {
  const placeholderValues: Record<string, string> = {
    'Search Name': '',
    'Document Content': '',
    'Concept Search Text': '',
    'Minimum Confidence Score for Concept Searches (%) (If Blank, System Default is 70%)':
      'select one',
    WorkProduct: 'select one',
    Analytics: '',
    'Include Family Members (If Blank, Default is N)': 'select one',
  };

  return headers.every((header) => {
    const value = row[header as keyof IUploadFileData];
    const normalizedValue =
      value === undefined || value === null ? '' : value.toString().trim();
    const placeholderValue = placeholderValues[header];
    if (placeholderValue === 'select one') {
      return normalizedValue === '' || normalizedValue === placeholderValue;
    } else {
      return normalizedValue === '';
    }
  });
};

//function to ensure all column data exists.
export const ensureFields = (data: IUploadFileData[]) => {
  return data.map((row: IUploadFileData) => {
    return {
      'Search Name': row['Search Name'] || '',
      'Document Content': row['Document Content'] || '',
      'Concept Search Text': row['Concept Search Text'] || '',
      'Minimum Confidence Score for Concept Searches (%) (If Blank, System Default is 70%)':
        row[
          'Minimum Confidence Score for Concept Searches (%) (If Blank, System Default is 70%)'
        ] || '',
      WorkProduct: row['WorkProduct'] || '',
      Analytics: row['Analytics'] || '',
      'Include Family Members (If Blank, Default is N)':
        row['Include Family Members (If Blank, Default is N)'] || '',
    };
  });
};

export const expectedHeaders = [
  'Search Name',
  'Document Content',
  'Concept Search Text',
  'Minimum Confidence Score for Concept Searches (%)\n(If Blank, System Default is 70%)',
  'WorkProduct',
  'Analytics',
  'Include Family Members\n(If Blank, Default is N)',
];

export const getExpandedIdsForNode = (
  nodes: TreeViewBaseItem<ExtendedTreeItemProps>[],
  targetId: string
): string[] => {
  const path: string[] = [];

  const dfs = (
    node: TreeViewBaseItem<ExtendedTreeItemProps>,
    ancestors: string[]
  ): boolean => {
    if (node.id === targetId) {
      path.push(...ancestors);
      return true;
    }

    if (node.children?.length) {
      for (const child of node.children) {
        if (dfs(child, [...ancestors, node.id])) {
          return true;
        }
      }
    }

    return false;
  };

  nodes.forEach((root) => dfs(root, []));

  return path;
};
